import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import React from 'react'

import {
  confirmConsumablesReceipt,
  fetchConsumableRoster,
  fetchConsumablesHistory,
  fetchMyCentres,
  saveConsumableRoster
} from 'api/procurement'
import constants from 'app/constants'
import { confirm, eventAlert, notify } from 'app/helpers'

import BudgetBanner from '../components/BudgetBanner'
import { downloadCsv, parseCsvFile } from '../components/exportUtils'
import {
  checkOverallBudget,
  overBudgetMessage,
  softOverspendNote
} from '../components/overallBudget'
import SopButton from '../components/SopButton'
import { SHOW_CSV_IMPORT, defaultOrderMonth, orderCentres } from '../constants'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  select: { minWidth: 180 },
  search: { minWidth: 220 },
  // Hide the number-input spinner arrows (the arrows were error-prone and could
  // wipe entered values). Keyboard entry + validation are unaffected.
  noSpin: {
    '& input[type=number]': { MozAppearance: 'textfield' },
    '& input[type=number]::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    },
    '& input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0
    }
  },
  qtyInput: { width: 70 },
  priceInput: { width: 84 },
  // Item gets the room; the numeric columns are kept tight so it all fits.
  itemCell: {
    minWidth: 240,
    maxWidth: 380,
    whiteSpace: 'normal',
    wordBreak: 'break-word'
  },
  narrowCol: { width: 70 },
  total: { fontWeight: 700 },
  // Trim the cell horizontal padding so the row's Total column stays inside the
  // frame instead of overflowing into a horizontal scroll. Target native td/th
  // (not .MuiTableCell-root — this build suffixes MUI global class names, so a
  // nested .MuiTableCell-root selector would not match).
  compactTable: {
    '& td, & th': {
      paddingLeft: theme.spacing(1.25),
      paddingRight: theme.spacing(1.25)
    }
  },
  // Page-level sticky header (2026-07-21): the column headers freeze at the top
  // of the screen on scroll-down for long rosters/lists.
  stickyHead: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100],
      boxShadow: 'inset 0 -1px 0 #e0ddd6'
    }
  }
}))

const num = (v) => {
  const n = Number(v)
  return Number.isNaN(n) ? 0 : n
}

const Consumables = ({ centreId: controlledCentreId, initialMonth } = {}) => {
  const classes = useStyles()
  // Centre supplied by the Center Team layout when controlled (picker hidden).
  const controlled = !!controlledCentreId

  const [centres, setCentres] = React.useState([])
  const [centreId, setCentreId] = React.useState(controlledCentreId || '')
  const [month, setMonth] = React.useState(
    initialMonth || defaultOrderMonth()
  )
  const [roster, setRoster] = React.useState(null) // {rows, budget, used, remaining}
  // edits keyed by consumable_item uuid
  const [qtyEdits, setQtyEdits] = React.useState({})
  const [priceEdits, setPriceEdits] = React.useState({})
  const [search, setSearch] = React.useState('')
  const [onlyWithQty, setOnlyWithQty] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [history, setHistory] = React.useState([])
  const [viewRoster, setViewRoster] = React.useState(null)
  const [adHoc, setAdHoc] = React.useState([])
  // receipt dialog: null = closed; true = open. Per-line rows live in
  // receiptLines: [{ id, item_name, receipt_status, receipt_note }].
  const [receipt, setReceipt] = React.useState(null)
  const [receiptLines, setReceiptLines] = React.useState([])
  const [receiptSaving, setReceiptSaving] = React.useState(false)

  const monthDate = `${month}-01`

  const loadHistory = React.useCallback(async () => {
    if (!centreId) return
    try {
      const res = await fetchConsumablesHistory({ centre: centreId })
      setHistory(res?.results || [])
    } catch (e) {
      setHistory([])
    }
  }, [centreId])

  React.useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const openView = async (m) => {
    try {
      const res = await fetchConsumableRoster({ centre: centreId, month: m })
      const rows = (res?.rows || []).filter((r) => Number(r.qty) > 0)
      setViewRoster({ month: m, rows })
    } catch (e) {
      /* ignore */
    }
  }

  React.useEffect(() => {
    if (controlled) {
      setCentreId(controlledCentreId)
      return
    }
    ;(async () => {
      try {
        const res = await fetchMyCentres()
        const list = orderCentres(res?.results || [])
        setCentres(list)
        if (list.length) setCentreId(list[0].uuid)
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to load centres',
          constants.ERROR,
          'Consumables'
        )
      }
    })()
  }, [controlled, controlledCentreId])

  const loadRoster = React.useCallback(async () => {
    if (!centreId) return
    setLoading(true)
    try {
      const res = await fetchConsumableRoster({
        centre: centreId,
        month: monthDate
      })
      setRoster(res)
      setQtyEdits({})
      setPriceEdits({})
      setAdHoc(
        (res.rows || [])
          .filter((r) => r.is_adhoc)
          .map((r) => ({
            consumable_item: r.consumable_item,
            name: r.name,
            unit: r.unit,
            qty: String(r.qty),
            unit_price: String(r.unit_price)
          }))
      )
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load roster',
        constants.ERROR,
        'Consumables'
      )
    } finally {
      setLoading(false)
    }
  }, [centreId, monthDate])

  React.useEffect(() => {
    loadRoster()
  }, [loadRoster])

  const frozen = !!roster?.frozen
  // Submit lifecycle mirrors program material: submitted/approved months are
  // locked for the centre; a rejected month is editable again (fix + resubmit).
  const status = roster?.status || 'draft'
  const submitted = status === 'submitted'
  const approved = status === 'approved'
  const rejected = status === 'rejected'
  // 'sent' = HQ dispatched the items; the centre confirms receipt. 'received' =
  // receipt confirmed (see roster.discrepancy). Both stay locked for editing.
  const sent = status === 'sent'
  const received = status === 'received'
  const locked = frozen || submitted || approved || sent || received

  const getQty = (row) =>
    Object.prototype.hasOwnProperty.call(qtyEdits, row.consumable_item)
      ? qtyEdits[row.consumable_item]
      : row.qty ?? 0
  const getPrice = (row) =>
    Object.prototype.hasOwnProperty.call(priceEdits, row.consumable_item)
      ? priceEdits[row.consumable_item]
      : row.unit_price ?? row.default_price ?? 0

  const liveTotal = React.useMemo(() => {
    if (!roster) return 0
    const rosterTotal = roster.rows
      .filter((r) => !r.is_adhoc)
      .reduce((sum, row) => sum + num(getQty(row)) * num(getPrice(row)), 0)
    const adhocTotal = adHoc.reduce(
      (s, r) => s + num(r.qty) * num(r.unit_price),
      0
    )
    return rosterTotal + adhocTotal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, qtyEdits, priceEdits, adHoc])

  const visibleRows = React.useMemo(() => {
    if (!roster) return []
    const q = search.trim().toLowerCase()
    return roster.rows.filter((row) => {
      if (row.is_adhoc) return false
      if (q && !row.name.toLowerCase().includes(q)) return false
      if (onlyWithQty && num(getQty(row)) <= 0) return false
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster, search, onlyWithQty, qtyEdits])

  const updateAdHoc = (i, patch) =>
    setAdHoc((prev) =>
      prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r))
    )
  const addAdHoc = () =>
    setAdHoc((prev) => [
      ...prev,
      { name: '', unit: '', qty: '', unit_price: '' }
    ])
  const removeAdHoc = (i) =>
    setAdHoc((prev) => prev.filter((_, idx) => idx !== i))

  const persist = async (status) => {
    if (!roster) return
    setSaving(true)
    try {
      const rosterRows = roster.rows
        .filter((r) => !r.is_adhoc)
        .map((row) => ({
          consumable_item: row.consumable_item,
          qty: num(getQty(row)),
          unit_price: num(getPrice(row))
        }))
      const adHocRows = adHoc
        .filter((r) => r.name && num(r.qty) > 0)
        .map((r) => ({
          consumable_item: r.consumable_item || '',
          name: r.name,
          unit: r.unit,
          qty: num(r.qty),
          unit_price: num(r.unit_price),
          is_adhoc: true
        }))
      const res = await saveConsumableRoster({
        centre: centreId,
        month: monthDate,
        rows: [...rosterRows, ...adHocRows],
        status
      })
      setRoster(res)
      setQtyEdits({})
      setPriceEdits({})
      notify(
        status === 'submitted' ? 'Consumables submitted' : 'Consumables saved',
        '',
        'success'
      )
      loadHistory()
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to save consumables',
        constants.ERROR,
        'Consumables'
      )
    } finally {
      setSaving(false)
    }
  }

  // Draft save: no budget gate (drafts don't count toward spend server-side).
  const onSave = () => persist('draft')

  // Open the per-line receipt dialog for a 'sent' month. Every ordered line
  // (qty > 0, roster item or ad-hoc) defaults to "received"; the centre flags
  // anything not received or wrong.
  const openReceipt = () => {
    const ordered = (roster?.rows || []).filter((r) => num(r.qty) > 0)
    setReceiptLines(
      ordered.map((r) => ({
        id: r.consumable_item,
        item_name: r.name,
        receipt_status:
          r.receipt_status && r.receipt_status !== 'pending'
            ? r.receipt_status
            : 'received',
        receipt_note: r.receipt_note || ''
      }))
    )
    setReceipt(true)
  }

  const updateReceiptLine = (i, patch) =>
    setReceiptLines((prev) =>
      prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l))
    )

  // Centre confirms receipt of a 'sent' month, line by line (a discrepancy
  // needs a note; not-received and discrepancy lines are flagged to HQ).
  const submitReceipt = async () => {
    const missingNote = receiptLines.filter(
      (l) => l.receipt_status === 'discrepancy' && !l.receipt_note.trim()
    )
    if (missingNote.length) {
      notify('Add a note for each item marked as a discrepancy', '', 'warning')
      return
    }
    const withIssues = receiptLines.some(
      (l) =>
        l.receipt_status === 'discrepancy' || l.receipt_status === 'not_received'
    )
    setReceiptSaving(true)
    try {
      await confirmConsumablesReceipt(
        centreId,
        monthDate,
        receiptLines.map((l) => ({
          id: l.id,
          receipt_status: l.receipt_status,
          receipt_note: l.receipt_note.trim()
        }))
      )
      notify(
        withIssues ? 'Receipt confirmed with issues' : 'Receipt confirmed',
        '',
        'success'
      )
      setReceipt(null)
      setReceiptLines([])
      loadRoster()
    } catch (error) {
      notify(error?.message || 'Failed to confirm receipt', '', 'error')
    } finally {
      setReceiptSaving(false)
    }
  }

  // CSV template lists every roster item so the centre can fill quantities
  // offline; upload matches rows back by item name (same pattern as PM / petty
  // cash). Only quantity (and an optional price) are taken from the file.
  const downloadTemplate = () =>
    downloadCsv(
      `consumables-template-${month}`,
      ['Item', 'Unit', 'Qty', 'Unit Price'],
      (roster?.rows || [])
        .filter((r) => !r.is_adhoc)
        .map((r) => [r.name, r.unit || '', '', num(getPrice(r))])
    )

  const onUploadCsv = async (e) => {
    const file = e.target.files && e.target.files[0]
    e.target.value = ''
    if (!file || !roster) return
    try {
      const data = await parseCsvFile(file)
      const byName = {}
      roster.rows
        .filter((r) => !r.is_adhoc)
        .forEach((r) => {
          byName[r.name.trim().toLowerCase()] = r
        })
      const nextQty = {}
      const nextPrice = {}
      let matched = 0
      data.forEach((row) => {
        const name = (row.Item || row.item || '').trim().toLowerCase()
        const r = byName[name]
        if (!r) return
        const qty = row.Qty ?? row.qty
        const price = row['Unit Price'] ?? row.unit_price
        if (qty !== undefined && qty !== '' && !Number.isNaN(Number(qty))) {
          nextQty[r.consumable_item] = String(Number(qty))
          matched += 1
        }
        if (
          price !== undefined &&
          price !== '' &&
          !Number.isNaN(Number(price))
        ) {
          nextPrice[r.consumable_item] = String(Number(price))
        }
      })
      if (!matched) {
        notify('No matching items found in the CSV', '', 'warning')
        return
      }
      setQtyEdits((prev) => ({ ...prev, ...nextQty }))
      setPriceEdits((prev) => ({ ...prev, ...nextPrice }))
      notify(
        `Loaded quantities for ${matched} item(s). Review and Submit.`,
        '',
        'success'
      )
    } catch (err) {
      eventAlert(
        err?.message || 'Could not read CSV',
        constants.ERROR,
        'Consumables'
      )
    }
  }

  // Submit: locks the month until HQ reopens. A single category may overspend
  // (warn but allow); the overall month total is a hard cap (blocks).
  const onSubmit = async () => {
    if (!roster) return
    // Every ordered item (qty > 0) must carry a unit price, else the budget is
    // understated. Catalogue prices auto-fill; the centre fills any gaps.
    const noPrice = []
    roster.rows
      .filter((r) => !r.is_adhoc)
      .forEach((row) => {
        if (num(getQty(row)) > 0 && !(num(getPrice(row)) > 0)) {
          noPrice.push(row.name)
        }
      })
    adHoc.forEach((r) => {
      if (r.name && num(r.qty) > 0 && !(num(r.unit_price) > 0)) {
        noPrice.push(r.name)
      }
    })
    if (noPrice.length) {
      notify(
        `Enter a unit price for: ${noPrice.slice(0, 5).join(', ')}${
          noPrice.length > 5 ? '…' : ''
        }`,
        '',
        'warning'
      )
      return
    }
    // Hard cap: block if the month's total across all categories goes over.
    const ob = await checkOverallBudget({
      centre: centreId,
      month: monthDate,
      category: 'consumables',
      projectedCategoryValue: liveTotal
    })
    if (ob.exceeded) {
      eventAlert(
        overBudgetMessage(ob, Dayjs(monthDate).format('MMMM YYYY'), 'order'),
        constants.ERROR,
        'Month budget exceeded'
      )
      return
    }
    const overspent = roster.budget != null && liveTotal > Number(roster.budget)
    const msg = overspent
      ? softOverspendNote('consumables') +
        ' Submit this month? It will be locked and sent to HQ for approval.'
      : 'Submit consumables for this month? It will be locked and sent to HQ for approval.'
    confirm(
      () => persist('submitted'),
      msg,
      () => {}
    )
  }

  return (
    <Paper className={`${classes.root} ${classes.noSpin}`} elevation={0}>
      <div className={classes.toolbar}>
        {!controlled && <Typography variant="h6">Consumables</Typography>}
        {!controlled && (
          <TextField
            select
            className={classes.select}
            size="small"
            variant="outlined"
            label="Centre"
            value={centreId}
            onChange={(e) => setCentreId(e.target.value)}
          >
            {centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          type="month"
          label="Month"
          size="small"
          variant="outlined"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <div className={classes.spacer} />
        {sent && (
          <Button
            variant="contained"
            color="primary"
            onClick={openReceipt}
          >
            Check &amp; confirm receipt
          </Button>
        )}
        {SHOW_CSV_IMPORT && !locked && (
          <>
            <Button size="small" variant="outlined" component="label">
              Upload CSV
              <input type="file" hidden accept=".csv" onChange={onUploadCsv} />
            </Button>
            <Button size="small" onClick={downloadTemplate}>
              Template
            </Button>
          </>
        )}
        <SopButton category="consumables" />
        <Button
          variant={onlyWithQty ? 'contained' : 'outlined'}
          color="primary"
          onClick={() => setOnlyWithQty((v) => !v)}
        >
          Review your order
        </Button>
        <Button
          variant="outlined"
          color="primary"
          disabled={saving || loading || locked}
          onClick={onSave}
        >
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saving || loading || locked}
          onClick={onSubmit}
        >
          Submit
        </Button>
      </div>

      {submitted && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#fff8e1', borderRadius: 8, color: '#8d6e00' }}
        >
          This month has been <strong>submitted</strong> and is now locked,
          awaiting HQ approval. Ask HQ to reopen it if you need to make changes.
        </Box>
      )}

      {approved && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#e8f5e9', borderRadius: 8, color: '#2e7d32' }}
        >
          This month has been <strong>approved by HQ</strong>. It's locked — ask
          HQ to reopen it if a change is needed.
        </Box>
      )}

      {rejected && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#ffebee', borderRadius: 8, color: '#c62828' }}
        >
          <strong>Rejected by HQ.</strong>
          {roster?.review_remarks ? ` ${roster.review_remarks}` : ''} Edit the
          list and submit again.
        </Box>
      )}

      {sent && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#e3f2fd', borderRadius: 8, color: '#1565c0' }}
        >
          HQ has <strong>sent</strong> this month's consumables to your centre.
          When they arrive, click <strong>Check &amp; confirm receipt</strong> at
          the top and mark each item received, not received, or received with a
          discrepancy.
        </Box>
      )}

      {received && (
        <Box
          mb={2}
          p={1.5}
          style={{
            background: roster?.discrepancy ? '#fff8e1' : '#e8f5e9',
            borderRadius: 8,
            color: roster?.discrepancy ? '#8d6e00' : '#2e7d32'
          }}
        >
          {roster?.discrepancy ? (
            <>
              <strong>Receipt confirmed with issues.</strong>
              {roster?.discrepancy_notes ? ` ${roster.discrepancy_notes}` : ''}{' '}
              HQ has been notified.
            </>
          ) : (
            <>
              <strong>Receipt confirmed</strong> — all items received. Thank you.
            </>
          )}
        </Box>
      )}

      {frozen && (
        <Box
          mb={2}
          p={1.5}
          style={{ background: '#ede7f6', borderRadius: 8, color: '#5e35b1' }}
        >
          This month has been <strong>frozen by HQ</strong> — consumables can no
          longer be edited until HQ unfreezes it.
        </Box>
      )}

      {roster && (
        <BudgetBanner
          label="This month"
          budget={roster.budget}
          used={liveTotal}
          remaining={
            roster.budget === null || roster.budget === undefined
              ? null
              : Number(roster.budget) - liveTotal
          }
        />
      )}

      <div className={classes.toolbar}>
        <TextField
          className={classes.search}
          size="small"
          variant="outlined"
          label="Search item"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={classes.spacer} />
        <Typography className={classes.total}>
          Total: ₹{liveTotal.toLocaleString('en-IN')}
        </Typography>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer style={{ overflow: 'visible' }}>
          <Table size="small" className={`${classes.compactTable} ${classes.stickyHead}`}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.narrowCol}>Sl No.</TableCell>
                <TableCell className={classes.itemCell}>Item</TableCell>
                <TableCell className={classes.narrowCol}>Unit</TableCell>
                <TableCell align="right" className={classes.narrowCol}>
                  Qty
                </TableCell>
                <TableCell align="right">Unit Price (₹)</TableCell>
                <TableCell align="right">Total (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, idx) => {
                const lineTotal = num(getQty(row)) * num(getPrice(row))
                return (
                  <TableRow key={row.consumable_item} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell className={classes.itemCell}>
                      {row.name}
                      {row.is_global && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ marginLeft: 6 }}
                        >
                          (global)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.unit}</TableCell>
                    <TableCell align="right">
                      <TextField
                        className={classes.qtyInput}
                        size="small"
                        variant="outlined"
                        type="number"
                        placeholder="0"
                        disabled={locked}
                        value={getQty(row)}
                        onChange={(e) => {
                          // Read the value BEFORE the updater runs. React pools
                          // synthetic events, so `e.target` is null by the time a
                          // functional setState callback executes — reading it in
                          // there crashed the whole page the moment anyone typed
                          // a quantity. Same bug that hit the Team Roster.
                          const v = e.target.value
                          setQtyEdits((prev) => ({
                            ...prev,
                            [row.consumable_item]: v
                          }))
                        }}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        className={classes.priceInput}
                        size="small"
                        variant="outlined"
                        type="number"
                        disabled={locked}
                        value={getPrice(row)}
                        onChange={(e) => {
                          const v = e.target.value
                          setPriceEdits((prev) => ({
                            ...prev,
                            [row.consumable_item]: v
                          }))
                        }}
                        inputProps={{ min: 0, style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {lineTotal.toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                )
              })}
              {isEmpty(visibleRows) && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="textSecondary">
                      No consumable items.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ad-hoc "one-off items" removed (Azam 2026-07-21): centres pick only
          from the shared consumables catalogue and can't add new items. */}
      {false && (
      <Box mt={3}>
        <div className={classes.toolbar}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            One-off items (not on the roster)
          </Typography>
          <div className={classes.spacer} />
          <Button
            size="small"
            variant="outlined"
            disabled={locked}
            onClick={addAdHoc}
          >
            + Add one-off item
          </Button>
        </div>
        {adHoc.length > 0 && (
          <Table size="small" className={classes.compactTable}>
            <TableHead>
              <TableRow>
                <TableCell>Item name</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Price (₹)</TableCell>
                <TableCell align="right">Total (₹)</TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {adHoc.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Item name"
                      value={r.name}
                      onChange={(e) => updateAdHoc(i, { name: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      style={{ width: 90 }}
                      placeholder="unit"
                      value={r.unit}
                      onChange={(e) => updateAdHoc(i, { unit: e.target.value })}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      style={{ width: 90 }}
                      value={r.qty}
                      onChange={(e) => updateAdHoc(i, { qty: e.target.value })}
                      inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      variant="outlined"
                      type="number"
                      style={{ width: 110 }}
                      value={r.unit_price}
                      onChange={(e) =>
                        updateAdHoc(i, { unit_price: e.target.value })
                      }
                      inputProps={{ min: 0, style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {(num(r.qty) * num(r.unit_price)).toLocaleString('en-IN')}
                  </TableCell>
                  <TableCell align="center">
                    <Button size="small" onClick={() => removeAdHoc(i)}>
                      ✕
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
      )}

      {roster && !isEmpty(roster.original_snapshot) ? (
        <ConsumablesDiff
          original={roster.original_snapshot}
          current={roster.rows || []}
        />
      ) : null}

      <Box mt={4}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Past months
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Total (₹)</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((h) => (
              <TableRow key={h.month} hover>
                <TableCell>{Dayjs(h.month).format('MMMM YYYY')}</TableCell>
                <TableCell align="right">{h.items}</TableCell>
                <TableCell align="right">
                  {Number(h.total).toLocaleString('en-IN')}
                </TableCell>
                <TableCell align="center">
                  <Button size="small" onClick={() => openView(h.month)}>
                    View
                  </Button>
                  {/* Edit only when still editable (draft/rejected); submitted/
                      approved and frozen months are View-only (#6). */}
                  {['draft', 'rejected'].includes(h.status) && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setMonth(h.month.slice(0, 7))}
                    >
                      Edit
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {isEmpty(history) && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No past months.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog
        open={!!viewRoster}
        onClose={() => setViewRoster(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Consumables —{' '}
          {viewRoster ? Dayjs(viewRoster.month).format('MMMM YYYY') : ''}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Price (₹)</TableCell>
                <TableCell align="right">Total (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(viewRoster?.rows || []).map((r) => (
                <TableRow key={r.consumable_item}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">{r.qty}</TableCell>
                  <TableCell align="right">{r.unit_price}</TableCell>
                  <TableCell align="right">
                    {(Number(r.qty) * Number(r.unit_price)).toLocaleString(
                      'en-IN'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!receipt}
        onClose={() => setReceipt(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Check &amp; confirm receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Mark each item <strong>Received</strong>,{' '}
            <strong>Not received</strong> (it never arrived), or{' '}
            <strong>Received with discrepancy</strong> (short, damaged, wrong
            item…) with a note. Not-received and discrepancy items are flagged to
            HQ per line.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell style={{ width: 150 }}>Receipt</TableCell>
                <TableCell>Note (if discrepancy)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receiptLines.map((l, i) => (
                <TableRow key={l.id || i}>
                  <TableCell>{l.item_name}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={l.receipt_status}
                      onChange={(e) =>
                        updateReceiptLine(i, { receipt_status: e.target.value })
                      }
                    >
                      <MenuItem value="received">Received</MenuItem>
                      <MenuItem value="not_received">Not received</MenuItem>
                      <MenuItem value="discrepancy">
                        Received with discrepancy
                      </MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder="What's wrong?"
                      disabled={l.receipt_status !== 'discrepancy'}
                      value={l.receipt_note}
                      onChange={(e) =>
                        updateReceiptLine(i, { receipt_note: e.target.value })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!receiptLines.length && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="textSecondary">
                      No ordered items to confirm.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceipt(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={receiptSaving || !receiptLines.length}
            onClick={submitReceipt}
          >
            {receiptSaving ? 'Saving…' : 'Confirm receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

// Original (first save of the month) vs current consumables list.
const ConsumablesDiff = ({ original, current }) => {
  const [open, setOpen] = React.useState(false)
  const rows = React.useMemo(() => {
    const keyOf = (r) =>
      (r.consumable_item && String(r.consumable_item)) ||
      String(r.name || '')
        .trim()
        .toLowerCase()
    const oMap = new Map((original || []).map((r) => [keyOf(r), r]))
    const cMap = new Map(
      (current || []).filter((r) => num(r.qty) > 0).map((r) => [keyOf(r), r])
    )
    const out = []
    oMap.forEach((o, k) => {
      const c = cMap.get(k)
      if (!c) {
        out.push({ key: k, kind: 'removed', name: o.name, o, c: null })
      } else if (
        num(o.qty) !== num(c.qty) ||
        num(o.unit_price) !== num(c.unit_price)
      ) {
        out.push({ key: k, kind: 'changed', name: c.name || o.name, o, c })
      }
    })
    cMap.forEach((c, k) => {
      if (!oMap.has(k))
        out.push({ key: k, kind: 'added', name: c.name, o: null, c })
    })
    return out
  }, [original, current])

  const TONE = {
    added: { background: '#e8f5e9', label: 'Added' },
    removed: { background: '#ffebee', label: 'Removed' },
    changed: { background: '#fff8e1', label: 'Changed' }
  }

  return (
    <Box mt={3}>
      <Button size="small" onClick={() => setOpen((v) => !v)}>
        {open ? '▾' : '▸'} Original vs current
        {rows.length
          ? ` (${rows.length} change${rows.length > 1 ? 's' : ''})`
          : ' (no changes)'}
      </Button>
      {open && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Change</TableCell>
              <TableCell>Item</TableCell>
              <TableCell align="right">Original qty × price</TableCell>
              <TableCell align="right">Current qty × price</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmpty(rows) ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No changes since the first save this month.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r.key}
                  style={{ background: TONE[r.kind].background }}
                >
                  <TableCell>{TONE[r.kind].label}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell align="right">
                    {r.o ? `${num(r.o.qty)} × ${num(r.o.unit_price)}` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {r.c ? `${num(r.c.qty)} × ${num(r.c.unit_price)}` : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default Consumables
